import { useEffect } from 'react';
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  LayersControl,
  useMap,
} from 'react-leaflet';
import type { LatLngBoundsExpression } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { VarietyRecord } from './types';
import { HeatmapLayer } from './HeatmapLayer';

function FitBounds({ records }: { records: VarietyRecord[] }) {
  const map = useMap();

  useEffect(() => {
    if (records.length === 0) return;
    const lats = records.map((r) => r.lat);
    const lons = records.map((r) => r.lon);
    const bounds: LatLngBoundsExpression = [
      [Math.min(...lats), Math.min(...lons)],
      [Math.max(...lats), Math.max(...lons)],
    ];
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [records, map]);

  return null;
}

interface MapViewProps {
  records: VarietyRecord[];
  selectedVariety: string;
  showModel: boolean;
}

export function MapView({ records, selectedVariety, showModel }: MapViewProps) {
  const isAll = selectedVariety === 'all';
  const heatmapRecords = !isAll
    ? records.filter((r) => r.variety === selectedVariety)
    : [];

  return (
    <MapContainer
      center={[9, 8]}
      zoom={6}
      style={{ height: '100%', width: '100%' }}
      scrollWheelZoom={true}
    >
      <LayersControl position="topright">
        <LayersControl.BaseLayer checked name="OpenStreetMap">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Elevation">
          <TileLayer
            attribution='&copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
            url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Satellite">
          <TileLayer
            attribution='&copy; <a href="https://www.esri.com">Esri</a>'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
        </LayersControl.BaseLayer>
      </LayersControl>

      <FitBounds records={records} />

      {records.map((record) => {
        const isSelected = !isAll && record.variety === selectedVariety;
        const color = isAll ? '#e53e3e' : isSelected ? '#e53e3e' : '#3b82f6';
        const radius = isAll ? 3 : isSelected ? 4 : 1;
        const fillOpacity = isAll ? 1 : isSelected ? 0.8 : 0.4;

        return (
          <CircleMarker
            key={record.rowIndex}
            center={[record.lat, record.lon]}
            radius={radius}
            pathOptions={{
              color,
              fillColor: color,
              fillOpacity,
              weight: 1,
            }}
          >
            <Popup>
              {isAll ? record.variety : `record ${record.rowIndex}`}
            </Popup>
          </CircleMarker>
        );
      })}

      {!isAll && showModel && heatmapRecords.length > 0 && (
        <HeatmapLayer records={heatmapRecords} />
      )}
    </MapContainer>
  );
}
